/**
 * `deriveAvatarInitials` — pure, identity-agnostic avatar-label helper.
 *
 * Derives a 1-2 letter avatar label from any display name. It carries NO
 * demo coupling: it takes a plain string (the real session's
 * `authState.displayName` in production) and returns a short uppercased
 * label for the avatar circle. Relocated out of the (retiring) demo
 * `session/demoMemberIdentity.ts` module during the D-060 client-side
 * rescission (032 T035) so its three live callers — `shell/MenuSheet.tsx`,
 * `routes/Profile.tsx`, `routes/profile/MemberProfileCard.tsx` — no longer
 * reach into a demo module for a presentation utility.
 *
 * Lives under `shell/` (alongside the other shell presentation utilities)
 * because all three callers render shell/profile chrome and the helper is
 * pure layout, not session or membership logic.
 */

/**
 * Derives a 1-2 letter avatar label from a display name. Splits on
 * whitespace, takes the first letter of up to two words, uppercases.
 *
 * Examples:
 *   "Demo Member"          → "DM"
 *   "Demo Admin"           → "DA"
 *   "Lone"                 → "L"
 *   ""                     → ""    (empty input yields an empty label;
 *                                   callers render a tinted placeholder
 *                                   circle for layout stability)
 *   "  Spaced  Out  "      → "SO"  (surrounding whitespace stripped)
 *   "First Second Third"   → "FS"  (only the first two words contribute)
 */
export function deriveAvatarInitials(displayName: string): string {
  const words = displayName
    .split(/\s+/u)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}
